import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';

interface Step {
    portrait: string;
    text: string;
}

interface IntroDialogProps {
    onEnd: () => void;
}

export const IntroDialog: React.FC<IntroDialogProps> = ({ onEnd }) => {
    const t = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);

    const steps: Step[] = [
        {
            portrait: './sprites/intro/portrait_oldman.png',
            text: t.tutorial.intro.step1
        },
        {
            portrait: './sprites/intro/portrait_ruins.png',
            text: t.tutorial.intro.step2
        },
        {
            portrait: './sprites/intro/portrait_corruption.png',
            text: t.tutorial.intro.step3
        },
        {
            portrait: './sprites/intro/portrait_captain.png',
            text: t.tutorial.intro.step4
        },
        {
            portrait: './sprites/intro/portrait_horde.png',
            text: t.tutorial.intro.step5
        },
        {
            portrait: './sprites/intro/portrait_horizon.png',
            text: t.tutorial.intro.step6
        },
    ];

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            onEnd();
        }
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '2rem'
        }}>
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{
                    backgroundColor: '#1c1917', // stone-900
                    padding: '2rem',
                    borderRadius: '0.5rem',
                    maxWidth: '600px',
                    width: '100%',
                    border: '2px solid #ca8a04',
                    fontFamily: '"Press Start 2P", cursive',
                    color: '#fff',
                    boxShadow: '0 0 40px rgba(0,0,0,0.5)'
                }}
            >
                <div style={{
                    height: '160px',
                    background: 'linear-gradient(to right, #1f2937, #000)',
                    borderRadius: '0.25rem',
                    marginBottom: '1.5rem',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: '2px solid rgba(202, 138, 4, 0.3)'
                }}>
                    <AnimatePresence mode="wait">
                        <motion.img
                            key={currentStep}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            src={steps[currentStep].portrait}
                            alt="Portrait"
                            style={{ height: '100%', width: 'auto', imageRendering: 'pixelated' }}
                            onError={(e) => {
                                // Fallback if image not found
                                e.currentTarget.style.display = 'none';
                            }}
                        />
                    </AnimatePresence>
                </div>

                <motion.div
                    key={currentStep}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{
                        minHeight: '120px',
                        marginBottom: '2rem',
                        fontSize: '0.75rem',
                        lineHeight: '1.8',
                        whiteSpace: 'pre-wrap'
                    }}
                >
                    {steps[currentStep].text}
                </motion.div>

                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleNext}
                        style={{
                            backgroundColor: currentStep === steps.length - 1 ? '#059669' : '#ca8a04',
                            color: '#fff',
                            padding: '1rem 2rem',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            fontFamily: '"Press Start 2P", cursive',
                            border: 'none',
                            borderRadius: '0.25rem',
                            cursor: 'pointer',
                            boxShadow: currentStep === steps.length - 1 ? '0 4px 0 #065f46' : '0 4px 0 #854d0e',
                            textTransform: 'uppercase',
                            width: '100%'
                        }}
                    >
                        {currentStep === steps.length - 1 ? t.tutorial.understood : t.tutorial.next}
                    </motion.button>
                </div>
            </motion.div>
        </div>
    );
};
